import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  FileText,
  Send,
  Download,
  RefreshCw,
  Plus,
  X,
  CheckCircle,
  Clock,
  ExternalLink,
  Search,
  Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: FileText },
  pending_signature: { label: 'Pending Signature', color: 'bg-amber-100 text-amber-700', icon: Clock },
  signed: { label: 'Signed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: X },
};

export default function Agreements() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState([]);
  const [eligibleCustomers, setEligibleCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEsignDialog, setShowEsignDialog] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Create form
  const [createForm, setCreateForm] = useState({
    customer_id: '',
    isp_share: 99,
    operator_share: 1,
    operator_code: ''
  });

  const canManage = user?.roles?.some(r => ['admin', 'onboarding'].includes(r));

  const fetchAgreements = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/agreements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAgreements(data.agreements || []);
      }
    } catch (error) {
      console.error('Error fetching agreements:', error);
      toast.error('Failed to fetch agreements');
    }
  }, [token]);

  const fetchEligibleCustomers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/agreements/eligible-customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEligibleCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Error fetching eligible customers:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAgreements(), fetchEligibleCustomers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAgreements, fetchEligibleCustomers]);

  const handleCreateAgreement = async () => {
    if (!createForm.customer_id) {
      toast.error('Please select a customer');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API}/api/agreements/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createForm)
      });

      if (response.ok) {
        toast.success('Agreement generated successfully');
        setShowCreateDialog(false);
        setCreateForm({ customer_id: '', isp_share: 99, operator_share: 1, operator_code: '' });
        fetchAgreements();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate agreement');
      }
    } catch (error) {
      console.error('Error creating agreement:', error);
      toast.error('Failed to generate agreement');
    } finally {
      setProcessing(false);
    }
  };

  const handleSendForEsign = async (agreement) => {
    setProcessing(true);
    try {
      const response = await fetch(`${API}/api/agreements/${agreement.agreement_id}/send-esign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Agreement sent for eSign');
        setSelectedAgreement({ ...agreement, esign_url: data.esign_url });
        setShowEsignDialog(true);
        fetchAgreements();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send for eSign');
      }
    } catch (error) {
      console.error('Error sending for eSign:', error);
      toast.error('Failed to send for eSign');
    } finally {
      setProcessing(false);
    }
  };

  const handleCheckStatus = async (agreement) => {
    try {
      const response = await fetch(`${API}/api/agreements/${agreement.agreement_id}/esign-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'signed') {
          toast.success('Agreement has been signed!');
          fetchAgreements();
        } else {
          toast.info(`Status: ${data.status || 'Pending'}`);
        }
      }
    } catch (error) {
      console.error('Error checking status:', error);
      toast.error('Failed to check status');
    }
  };

  const handleDownloadPdf = async (agreement) => {
    try {
      const response = await fetch(`${API}/api/agreements/${agreement.agreement_id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Agreement_${agreement.company_name || 'Customer'}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        toast.error('Failed to download PDF');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleCancelAgreement = async (agreement) => {
    if (!window.confirm('Are you sure you want to cancel this agreement?')) return;

    try {
      const response = await fetch(`${API}/api/agreements/${agreement.agreement_id}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Agreement cancelled');
        fetchAgreements();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel agreement');
      }
    } catch (error) {
      console.error('Error cancelling agreement:', error);
      toast.error('Failed to cancel agreement');
    }
  };

  const filteredAgreements = agreements.filter(a => {
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    const matchesSearch = 
      a.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.agreement_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.operator_code?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const stats = {
    total: agreements.length,
    draft: agreements.filter(a => a.status === 'draft').length,
    pending: agreements.filter(a => a.status === 'pending_signature').length,
    signed: agreements.filter(a => a.status === 'signed').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agreements-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Interconnection Agreements</h1>
          <p className="text-slate-500 text-sm">Manage agreements for Partners and Bulk Bandwidth customers</p>
        </div>
        {canManage && (
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="flex items-center gap-2"
            data-testid="create-agreement-btn"
          >
            <Plus className="h-4 w-4" />
            Generate Agreement
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-50">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
            <div className="text-sm text-slate-500">Total Agreements</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-50">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-600">{stats.draft}</div>
            <div className="text-sm text-slate-500">Draft</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
            <div className="text-sm text-amber-600">Pending Signature</div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-emerald-600">{stats.signed}</div>
            <div className="text-sm text-emerald-600">Signed</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by company, agreement ID, or operator code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-agreements-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_signature">Pending Signature</SelectItem>
                <SelectItem value="signed">Signed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Agreements Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Agreements ({filteredAgreements.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredAgreements.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No agreements found</p>
              {canManage && (
                <Button 
                  variant="link" 
                  onClick={() => setShowCreateDialog(true)}
                  className="mt-2"
                >
                  Generate your first agreement
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Operator Code</TableHead>
                    <TableHead>Revenue Split</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAgreements.map((agreement) => {
                    const StatusIcon = STATUS_CONFIG[agreement.status]?.icon || FileText;
                    return (
                      <TableRow key={agreement.agreement_id} data-testid={`agreement-row-${agreement.agreement_id}`}>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              <a href={`/customers/${agreement.customer_id}`} onClick={(e) => { e.preventDefault(); navigate(`/customers/${agreement.customer_id}`); }} className="text-indigo-600 hover:text-indigo-800 hover:underline">
                                {agreement.company_name || 'N/A'}
                              </a>
                            </div>
                            <div className="text-xs text-slate-500">{agreement.contact_name}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="capitalize text-sm">{agreement.customer_type?.replace('_', ' ')}</span>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                            {agreement.operator_code}
                          </code>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {agreement.isp_share}:{agreement.operator_share}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[agreement.status]?.color || 'bg-slate-100'}`}>
                            <StatusIcon className="h-3 w-3" />
                            {STATUS_CONFIG[agreement.status]?.label || agreement.status}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500">
                            {new Date(agreement.created_at).toLocaleDateString()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadPdf(agreement)}
                              title="Download PDF"
                              data-testid={`download-pdf-${agreement.agreement_id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            
                            {agreement.status === 'draft' && canManage && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSendForEsign(agreement)}
                                  disabled={processing}
                                  title="Send for eSign"
                                  className="text-blue-600 hover:text-blue-700"
                                  data-testid={`send-esign-${agreement.agreement_id}`}
                                >
                                  <Send className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCancelAgreement(agreement)}
                                  title="Cancel"
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            
                            {agreement.status === 'pending_signature' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCheckStatus(agreement)}
                                  title="Check Status"
                                  className="text-amber-600 hover:text-amber-700"
                                >
                                  <RefreshCw className="h-4 w-4" />
                                </Button>
                                {agreement.esign_url && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => window.open(agreement.esign_url, '_blank')}
                                    title="Open eSign Link"
                                    className="text-blue-600 hover:text-blue-700"
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                            
                            {agreement.status === 'signed' && (
                              <span className="text-emerald-600 text-xs font-medium px-2">
                                Completed
                              </span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Agreement Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Interconnection Agreement</DialogTitle>
            <DialogDescription>
              Create an agreement for Partner or Bulk Bandwidth customers
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Customer *</Label>
              <Select 
                value={createForm.customer_id} 
                onValueChange={(v) => setCreateForm({...createForm, customer_id: v})}
              >
                <SelectTrigger data-testid="select-customer">
                  <SelectValue placeholder="Select a customer" />
                </SelectTrigger>
                <SelectContent>
                  {eligibleCustomers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>
                      <div className="flex items-center gap-2">
                        <span>{c.company_name}</span>
                        <span className="text-xs text-slate-500 capitalize">
                          ({c.customer_type?.replace('_', ' ')})
                        </span>
                        {c.agreement_signed && (
                          <CheckCircle className="h-3 w-3 text-emerald-500" />
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {eligibleCustomers.length === 0 && (
                <p className="text-xs text-amber-600">
                  No eligible customers found. Only Partner and Bulk Bandwidth customers can have agreements.
                </p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>ISP Share (%)</Label>
                <Input
                  type="number"
                  value={createForm.isp_share}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setCreateForm({
                      ...createForm, 
                      isp_share: val,
                      operator_share: 100 - val
                    });
                  }}
                  min="0"
                  max="100"
                />
              </div>
              <div className="space-y-2">
                <Label>Operator Share (%)</Label>
                <Input
                  type="number"
                  value={createForm.operator_share}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setCreateForm({
                      ...createForm, 
                      operator_share: val,
                      isp_share: 100 - val
                    });
                  }}
                  min="0"
                  max="100"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Operator Code (Optional)</Label>
              <Input
                placeholder="Auto-generated if empty"
                value={createForm.operator_code}
                onChange={(e) => setCreateForm({...createForm, operator_code: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAgreement} 
              disabled={processing || !createForm.customer_id}
              data-testid="generate-agreement-submit"
            >
              {processing ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><FileText className="h-4 w-4 mr-2" /> Generate Agreement</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* eSign URL Dialog */}
      <Dialog open={showEsignDialog} onOpenChange={setShowEsignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-600" />
              Agreement Sent for eSign
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-emerald-700 text-sm">
                The agreement has been sent to <strong>{selectedAgreement?.contact_name}</strong> for digital signature.
              </p>
            </div>
            
            {selectedAgreement?.esign_url && (
              <div className="space-y-2">
                <Label>eSign Link</Label>
                <div className="flex gap-2">
                  <Input 
                    value={selectedAgreement.esign_url} 
                    readOnly 
                    className="text-xs"
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(selectedAgreement.esign_url);
                      toast.success('Link copied!');
                    }}
                  >
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Share this link with the customer to sign the agreement.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEsignDialog(false)}>
              Close
            </Button>
            {selectedAgreement?.esign_url && (
              <Button onClick={() => window.open(selectedAgreement.esign_url, '_blank')}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Open eSign Page
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
